/**
 * A location as a form holds it: the text standing in the box plus the
 * autocomplete pick behind it, if the text came from one. Shared by the idea
 * and event form cores — both offer the same box, prefill their name from the
 * same rule, and send the same location wire shape.
 *
 * Pure: no React, no Google SDK, so it tests in node.
 */

import { PostLocationType } from '@orbit/api-types'

/** A place Google knows, as the Place search hands it over. */
export interface GooglePlace {
	placeId: string
	primaryText: string
	secondaryText: string
	fullText: string
	types: string[]
}

/** A location as the API hands it back — only the parts a Draft reads. */
export interface LocationLike {
	fullText: string
	primaryText?: string | null
	secondaryText?: string | null
	googlePlaceId?: string | null
	googleTypes?: string[] | null
}

export interface PlaceDraft {
	/** What stands in the location box. */
	text: string
	/** The Google place picked behind that text, or null for free text. */
	googlePlace: GooglePlace | null
}

/**
 * Wire-contract bound on a location's text, mirrored by name from
 * `@orbit/api-types` because that package is not browser-safe at runtime;
 * pinned by the create-trip flow's `contract.test.ts`. The server rejects a
 * blank or longer fullText, so a form refuses it first.
 */
export const MAX_LOCATION_FULL_TEXT_LENGTH = 255

/** Why a typed place cannot be a destination. */
export type PlaceTextProblem = 'blank' | 'tooLong'

/** The contract's own rule on a place's text, or null when the text passes. */
export const placeTextProblem = (text: string): PlaceTextProblem | null => {
	const trimmed = text.trim()
	if (trimmed.length === 0) return 'blank'
	if (trimmed.length > MAX_LOCATION_FULL_TEXT_LENGTH) return 'tooLong'
	return null
}

/** How long a name a picked place may prefill — the same cap as iOS. */
export const PLACE_NAME_MAX = 75

const capped = (name: string): string => name.substring(0, PLACE_NAME_MAX).trim()

export const emptyPlace = (): PlaceDraft => ({ text: '', googlePlace: null })

/**
 * A saved location back into a Draft. Only a Google-backed location restores a
 * pick; free text comes back as text alone, which is all it ever was.
 */
export const placeFromLocation = (location: LocationLike | null | undefined): PlaceDraft => ({
	text: location?.primaryText || '',
	googlePlace: location?.googlePlaceId
		? {
				placeId: location.googlePlaceId,
				primaryText: location.primaryText || '',
				secondaryText: location.secondaryText || '',
				fullText: location.fullText,
				types: location.googleTypes || [],
			}
		: null,
})

/** Text typed by hand — whatever was picked no longer describes what the box says. */
export const placeTyped = (text: string): PlaceDraft => ({ text, googlePlace: null })

/** A pick from the autocomplete list, which also fills the box; `text` overrides what it fills in. */
export const placePicked = (googlePlace: GooglePlace, text: string = googlePlace.fullText): PlaceDraft => ({
	text,
	googlePlace,
})

/**
 * The name a place gives whatever it is attached to: a picked place's own name
 * (its primary text, else its full text), or the text typed in. Capped like a
 * prefilled name.
 */
export const placeName = (place: PlaceDraft): string =>
	capped(place.googlePlace ? place.googlePlace.primaryText || place.googlePlace.fullText : place.text)

/** The second line to show under the box — a picked place's address, if there is one. */
export const placeSecondaryText = (place: PlaceDraft): string | undefined =>
	place.googlePlace?.secondaryText || undefined

/** The location a Draft sends, or nothing at all when the box is empty. */
export const placeToPayload = (place: PlaceDraft): PostLocationType | undefined => {
	if (place.googlePlace) {
		return {
			fullText: place.googlePlace.fullText,
			primaryText: place.googlePlace.primaryText,
			secondaryText: place.googlePlace.secondaryText || undefined,
			googlePlaceId: place.googlePlace.placeId,
			googleTypes: place.googlePlace.types,
		}
	}
	const trimmed = place.text.trim()
	return trimmed ? { fullText: trimmed, primaryText: trimmed, googleTypes: [] } : undefined
}

/** Two locations are the same when they would go over the wire the same way. */
export const samePlacePayload = (
	a: PostLocationType | null | undefined,
	b: PostLocationType | null | undefined,
): boolean => {
	if (!a || !b) return !a && !b
	return (
		a.fullText === b.fullText &&
		a.primaryText === b.primaryText &&
		a.secondaryText === b.secondaryText &&
		a.googlePlaceId === b.googlePlaceId &&
		a.googleTypes.join(' ') === b.googleTypes.join(' ')
	)
}

/**
 * The name an entry carries after picking a place: an entry with no name, or
 * one still holding the place it had before, follows the new pick; a name the
 * traveller wrote is never overwritten. Both names are `placeName`'s.
 */
export const nameAfterPick = (name: string, previous: PlaceDraft, picked: PlaceDraft): string => {
	const trimmed = name.trim()
	return trimmed === '' || trimmed === placeName(previous) ? placeName(picked) : name
}
